import { useFormContext, useWatch } from 'react-hook-form'
import { QuestionRulesConditions } from '../components/Drawer/Views/DrawerViewAddQuestionRule'
import { useReport } from '../pages/Questionnaire/ReportContext'
import { QuestionRule, RequestItem } from '../types/resourceExplorer'

export interface TriggerInfo {
  questionText: string
  answerValue: string
  questionIndex: string
  isSkipped?: boolean
  isFileUploaded?: boolean
}

const useExcludedQuestionTrigger = (requestItem: RequestItem, rules?: QuestionRule[]): TriggerInfo | null => {
  const { request } = useReport()
  const { control } = useFormContext()

  const formValues = useWatch({ control })

  if (!rules?.length || !request?.sections) return null

  const activeRule = rules.find(rule => {
    const triggerRequestItem = request.sections
      ?.flatMap(section => section.items)
      .find(item => item.questionId === rule.questionId)

    if (!triggerRequestItem) return false

    const targetsCurrentQuestion = rule.sectionQuestionTargets.some(
      target => target.targetQuestionId === requestItem.questionId,
    )

    if (!targetsCurrentQuestion) return false
    if (!formValues) return false

    if (rule.conditionValue === QuestionRulesConditions.Skipped) {
      const cannotAnswer = formValues[triggerRequestItem.id]?.cannotAnswer
      return cannotAnswer === true
    }

    if (rule.conditionValue === QuestionRulesConditions.FileUploaded) {
      const fileUploaded = formValues[triggerRequestItem.id]?.uploadReferences?.originalFileName
      return Boolean(fileUploaded)
    }

    const triggerAnswer = formValues[triggerRequestItem.id]?.selectedOptions
    const triggerAnswerArray = Array.isArray(triggerAnswer) ? triggerAnswer : [triggerAnswer]
    return triggerAnswerArray.includes(rule.conditionValue)
  })

  if (!activeRule) return null

  let questionIndex = ''
  let questionText = ''

  const sections = request.sections.map((section, sectionIdx) => ({
    ...section,
    sectionIdx,
  }))

  const foundItem = sections
    .flatMap(section =>
      section.items.map((item, itemIdx) => ({
        item,
        sectionIdx: section.sectionIdx,
        itemIdx,
      })),
    )
    .find(({ item }) => item.questionId === activeRule.questionId)

  if (foundItem) {
    const { item, sectionIdx, itemIdx } = foundItem
    questionIndex = `${sectionIdx + 1}.${itemIdx + 1}`
    questionText = item.description
  }

  return {
    questionText,
    answerValue: activeRule.conditionValue,
    questionIndex,
    isSkipped: activeRule.conditionValue === QuestionRulesConditions.Skipped,
    isFileUploaded: activeRule.conditionValue === QuestionRulesConditions.FileUploaded,
  }
}

export default useExcludedQuestionTrigger
